import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { errorText } from "../lib/errors.ts";
import { useStuff } from "../lib/stuff-queries.ts";
import { Icon } from "./Icon.tsx";
import { ErrorState, Loading } from "./Status.tsx";
import { StuffSheet } from "./StuffSheet.tsx";

/** The address keeps the open stuff (?peek=<id>): Back closes the drawer, a reload keeps it. */
const PARAM = "peek";

interface Drawer {
  open: (id: string) => void;
  close: () => void;
}
const Ctx = createContext<Drawer>({ open: () => undefined, close: () => undefined });

/** Opens a stuff over the current page, from the left, without leaving it. */
export const useStuffDrawer = () => useContext(Ctx);

function DrawerPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const stuff = useStuff(id);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (typeof d.showModal === "function" && !d.open) d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className="drawer"
      aria-labelledby="drawer-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // A click on the backdrop (the dialog itself, outside its panel) closes it.
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="drawer-panel">
        <div className="drawer-bar">
          <button type="button" className="btn btn-quiet btn-small drawer-close" onClick={onClose} aria-label={t("viewer.close")}>
            <Icon name="chevronLeft" />
            {t("drawer.close")}
          </button>
        </div>
        <div className="drawer-body">
          {stuff.isPending ? (
            <Loading />
          ) : stuff.error ? (
            <ErrorState message={errorText(t, stuff.error)} onRetry={() => void stuff.refetch()} />
          ) : (
            <StuffSheet stuff={stuff.data} titleId="drawer-title" />
          )}
        </div>
      </div>
    </dialog>
  );
}

export function StuffDrawerProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get(PARAM);
  // Opened from this visit: closing goes back one step, so Back and the close button agree.
  const pushed = useRef(false);

  const open = useCallback(
    (stuffId: string) => {
      const replace = params.has(PARAM);
      if (!replace) pushed.current = true;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(PARAM, stuffId);
          return next;
        },
        { replace },
      );
    },
    [params, setParams],
  );
  const close = useCallback(() => {
    if (pushed.current) {
      pushed.current = false;
      navigate(-1);
      return;
    }
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PARAM);
        return next;
      },
      { replace: true },
    );
  }, [navigate, setParams]);
  // Back closed it: nothing left to go back from.
  useEffect(() => {
    if (!id) pushed.current = false;
  }, [id]);

  const value = useMemo(() => ({ open, close }), [open, close]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {id && <DrawerPanel key={id} id={id} onClose={close} />}
    </Ctx.Provider>
  );
}

/**
 * A link to a stuff that opens it in the drawer; a middle click or Ctrl/Cmd-click
 * still opens the full page in a new tab.
 */
export function StuffLink({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  const { open } = useStuffDrawer();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    open(id);
  };
  return (
    <Link to={`/stuffs/${id}`} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}
